import { defaultTimeZone, defaultLocales } from './constants';
import { isDate } from './isDate';
import { isInvalidDate } from './isInvalidDate';

export type DateOnlyStringOptions = {
  timeZone?: string;
  /**
   * When set to true, the returned string will omit the year.
   * e.g. "Jun 3" instead of "Jun 3, 2021"
   */
  omitYear?: boolean;
};

/**
 * Converts a Date object into a date only string.
 *
 * e.g. Jun 3, 2021 or Jun 3 if `omitYear` is true.
 *
 * @param {*} date a Date object you want to convert into a string - gracefully handles any input
 * @param {object} options -
 * @param {string} options.timeZone - the timezone you want date displayed in, defaults to current user's time zone
 * @param {boolean} options.omitYear - if true, display the date without the year
 * @returns {string} a formatted date string
 */
export const dateOnlyString = (
  date: unknown,
  { timeZone = defaultTimeZone, omitYear = false }: DateOnlyStringOptions = {},
): string => {
  if (!isDate(date) || isInvalidDate(date)) {
    return '';
  }

  // Conditionally build formatting options based on the omitYear flag.
  const formatOptions: Intl.DateTimeFormatOptions = omitYear
    ? { month: 'short', day: 'numeric', timeZone }
    : { dateStyle: 'medium', timeZone };

  try {
    return new Intl.DateTimeFormat(defaultLocales, formatOptions).format(date);
  } catch (error: unknown) {
    console.error(error); // eslint-disable-line no-console
    return ''; // handle bad input
  }
};
